import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useListClientUsersQuery, useDeactivateClientUserMutation, useReactivateClientUserMutation } from '../../app/api';
import { useIsMobile, gemstoneInfo, GEMSTONE_OPTIONS, workflowStageInfo } from '../utils';
import PropTypes from "prop-types";

const work = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  ink:       '#0a2947',
  muted:     '#6b7280',
  hairline:  '#dde2e8',
  hairlineStrong: '#c4cbd4',
  surface:   '#ffffff',
  canvas:    '#f3f5f8',
  green:     '#1a7f4e',
  red:       '#b00020',
};

const LETTERS = ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];

function selectStyle() {
  return {
    border: `1px solid ${work.hairline}`, borderRadius: 6, padding: '0.4rem 0.55rem',
    fontSize: '0.82rem', color: work.ink, background: work.surface, cursor: 'pointer',
  };
}

function StagePill({ stage }) {
  const s = workflowStageInfo(stage);
  if (!s) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
      fontWeight: 700, fontSize: '0.72rem', color: work.ink, whiteSpace: 'nowrap',
    }}>
      <i className="bi bi-compass" style={{ color: s.color, fontSize: '0.8rem' }} />
      {s.label}
    </span>
  );
}
StagePill.propTypes = { stage: PropTypes.string };

function DocCountBadge({ count }) {
  return (
    <span
      title={`${count} uploaded document${count === 1 ? '' : 's'}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
        fontWeight: 700, fontSize: '0.72rem', color: count > 0 ? work.ink : work.muted,
        whiteSpace: 'nowrap',
      }}
    >
      <i className="bi bi-file-earmark-text" style={{ fontSize: '0.8rem' }} />
      {count}
    </span>
  );
}
DocCountBadge.propTypes = { count: PropTypes.number.isRequired };

// Beta/UAT household — never a real client. Uploads route to a separate,
// deletable S3 bucket instead of the WORM-locked production one.
function TestBadge() {
  return (
    <span
      title="Test household — not a real client. Documents go to the deletable beta-test bucket."
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
        background: 'rgba(208,155,44,0.18)', color: work.goldenrod,
        fontWeight: 700, fontSize: '0.65rem',
        textTransform: 'uppercase', letterSpacing: '0.5px',
        padding: '0.15rem 0.5rem', borderRadius: 4, whiteSpace: 'nowrap',
      }}
    >
      <i className="bi bi-flask" /> Test
    </span>
  );
}

// Soft-deleted for compliance — blocked from login, hidden from the default
// list, but every document/request/household record is untouched.
function DeactivatedBadge() {
  return (
    <span
      title="Deactivated — can no longer log in. Their document history is preserved for compliance."
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
        background: 'rgba(176,0,32,0.1)', color: work.red,
        fontWeight: 700, fontSize: '0.65rem',
        textTransform: 'uppercase', letterSpacing: '0.5px',
        padding: '0.15rem 0.5rem', borderRadius: 4, whiteSpace: 'nowrap',
      }}
    >
      <i className="bi bi-slash-circle" /> Deactivated
    </span>
  );
}

// Association → display label (mirrors the wizard's ASSOCIATIONS).
const ASSOC_LABELS = {
  primary: 'Primary', spouse: 'Spouse', domestic_partner: 'Domestic Partner',
  child: 'Child', parent: 'Parent', sibling: 'Sibling', dependent: 'Dependent',
  beneficiary: 'Beneficiary', trustee: 'Trustee', power_of_attorney: 'Power of Attorney',
  executor: 'Executor', business_partner: 'Business Partner', other: 'Other',
};

function fullName(first, last, fallback) {
  const joined = `${first || ''} ${last || ''}`.trim();
  return joined || fallback || '—';
}

// A client that hasn't been published/activated is still going through the
// new-client setup process — surface that as "In progress".
function StatusPill({ isActive }) {
  const label = isActive ? 'Active' : 'In progress';
  const bg = isActive ? 'rgba(26,127,78,0.12)' : 'rgba(208,155,44,0.18)';
  const color = isActive ? work.green : work.goldenrod;
  return (
    <span style={{
      background: bg, color,
      fontWeight: 700, fontSize: '0.65rem',
      textTransform: 'uppercase', letterSpacing: '0.5px',
      padding: '0.15rem 0.5rem', borderRadius: 4,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}
StatusPill.propTypes =  {
  isActive: PropTypes.bool,
}

function GemstoneBadge({ gemstone }) {
  const g = gemstoneInfo(gemstone);
  if (!g) return null;
  return (
    <span
      title={g.label}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
        fontWeight: 700, fontSize: '0.72rem', color: work.ink,
        whiteSpace: 'nowrap',
      }}
    >
      <i className="bi bi-suit-diamond-fill" style={{ color: g.color, fontSize: '0.85rem' }} />
      {g.label}
    </span>
  );
}
GemstoneBadge.propTypes = {
  gemstone: PropTypes.string,
};

function AssocBadge({ association }) {
  const label = ASSOC_LABELS[association];
  if (!label) return null;
  const isPrimary = association === 'primary';
  return (
    <span style={{
      background: isPrimary ? work.deepBlue : work.canvas,
      color: isPrimary ? 'white' : work.muted,
      border: `1px solid ${work.hairline}`,
      fontWeight: 700, fontSize: '0.62rem',
      textTransform: 'uppercase', letterSpacing: '0.5px',
      padding: '0.1rem 0.45rem', borderRadius: 999, whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}
AssocBadge.propTypes = { association: PropTypes.string };

export default function Clients() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { data: clients = [], isLoading } = useListClientUsersQuery();
  const [deactivateClient, { isLoading: isDeactivating }] = useDeactivateClientUserMutation();
  const [reactivateClient, { isLoading: isReactivating }] = useReactivateClientUserMutation();
  const isActing = isDeactivating || isReactivating;
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState(''); // '' | 'active' | 'in_progress'
  const [advisorFilter, setAdvisorFilter] = useState(''); // '' = all advisors
  const [gemstoneFilter, setGemstoneFilter] = useState('');
  const [testFilter, setTestFilter] = useState(''); // '' | 'test' | 'real'
  const [deactivatedFilter, setDeactivatedFilter] = useState('active'); // 'active' | 'deactivated' | 'all'
  const [letter, setLetter] = useState('');
  const [sort, setSort] = useState('recent'); // 'recent' | 'oldest' | 'name'
  const [expanded, setExpanded] = useState(() => new Set()); // household keys manually expanded
  // { client, type: 'deactivate' | 'reactivate' } queued for confirmation
  const [pendingAction, setPendingAction] = useState(null);
  const [actionError, setActionError] = useState(null);

  const toggleExpand = (key) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Group clients into households. Solo clients (no household_id) each form a
  // single-member group. Members are ordered primary-first, then by name.
  const groups = useMemo(() => {
    const map = new Map();
    for (const c of clients) {
      const key = c.household_id ? `h${c.household_id}` : `s${c.id}`;
      if (!map.has(key)) {
        map.set(key, { key, household_id: c.household_id || null, label: c.household_label || null, members: [] });
      }
      map.get(key).members.push(c);
    }
    const memberSort = (a, b) => {
      if ((a.association === 'primary') !== (b.association === 'primary')) return a.association === 'primary' ? -1 : 1;
      return fullName(a.last_name, a.first_name, a.email).localeCompare(fullName(b.last_name, b.first_name, b.email));
    };
    const list = Array.from(map.values()).map(g => {
      const members = [...g.members].sort(memberSort);
      const primary = members.find(m => m.association === 'primary') || members.find(m => m.email) || members[0];
      const displayName = g.label || (primary?.last_name ? `${primary.last_name} Household` : fullName(primary?.first_name, primary?.last_name, primary?.email));
      const docCount = members.reduce((sum, m) => sum + (m.uploaded_document_count || 0), 0);
      return { ...g, members, primary, displayName, isActive: members.some(m => m.is_active), docCount };
    });
    // Newest household first (client list already comes newest-first; use the
    // most-recent member's created as the household's sort key).
    return list.sort((a, b) => {
      const at = a.members[0]?.created || '';
      const bt = b.members[0]?.created || '';
      return bt.localeCompare(at);
    });
  }, [clients]);

  const memberHay = (m) =>
    [m.first_name, m.last_name, m.email, m.hubspot_contact_id, m.advisor_name, m.advisor_name_2]
      .filter(Boolean).join(' ').toLowerCase();

  // Alphabetical jump strip groups by household last name (falls back to the
  // display name for name-only/solo entries without one).
  const letterOf = (g) => {
    const base = (g.primary?.last_name || g.displayName || '').trim();
    const ch = base.charAt(0).toUpperCase();
    return /[A-Z]/.test(ch) ? ch : '#';
  };

  const q = query.trim().toLowerCase();

  // Advisor options come from every household, not the scoped set — otherwise
  // picking one advisor would empty the dropdown that made the choice.
  const presentAdvisors = useMemo(() => {
    const names = new Set();
    groups.forEach(g => g.members.forEach((m) => {
      if (m.advisor_name) names.add(m.advisor_name);
      if (m.advisor_name_2) names.add(m.advisor_name_2);
    }));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [groups]);

  // Scope to the selected advisor first (a household belongs to an advisor if
  // any member carries them as advisor_name or advisor_name_2), so the
  // gemstone/test facets below only offer values that survive the choice.
  const scopedGroups = useMemo(() => {
    if (!advisorFilter) return groups;
    return groups.filter(g =>
      g.members.some(m => m.advisor_name === advisorFilter || m.advisor_name_2 === advisorFilter),
    );
  }, [groups, advisorFilter]);

  // Only offer gemstone options that actually appear among the (advisor-scoped)
  // households, mirroring the document browser's facet-gated filter dropdowns.
  const presentGemstones = useMemo(() => {
    const keys = new Set(scopedGroups.map(g => g.primary?.gemstone).filter(Boolean));
    return GEMSTONE_OPTIONS.filter(g => keys.has(g.key));
  }, [scopedGroups]);

  // Status/gemstone/search filters, applied before the letter filter so the
  // alphabet strip's enabled letters reflect the other active filters.
  const preLetterGroups = useMemo(() => {
    let result = scopedGroups;
    if (status) {
      result = result.filter(g => (status === 'active' ? g.isActive : !g.isActive));
    }
    if (gemstoneFilter) {
      result = result.filter(g => g.primary?.gemstone === gemstoneFilter);
    }
    if (testFilter) {
      result = result.filter(g => (testFilter === 'test' ? g.primary?.is_test : !g.primary?.is_test));
    }
    if (deactivatedFilter === 'active') {
      // A household stays visible as long as at least one member is still
      // active — a fully-deactivated household (every member gone) is the
      // only thing hidden by the default view.
      result = result.filter(g => g.members.some(m => !m.deactivated_at));
    } else if (deactivatedFilter === 'deactivated') {
      result = result.filter(g => g.members.some(m => m.deactivated_at));
    }
    if (q) {
      // A household matches if its name matches or ANY member matches — so
      // searching a spouse/beneficiary surfaces their whole household.
      result = result.filter(g =>
        (g.label || '').toLowerCase().includes(q) ||
        g.displayName.toLowerCase().includes(q) ||
        g.members.some(m => memberHay(m).includes(q)),
      );
    }
    return result;
  }, [scopedGroups, status, gemstoneFilter, testFilter, deactivatedFilter, q]);

  const letterSet = useMemo(() => new Set(preLetterGroups.map(letterOf)), [preLetterGroups]);

  const filteredGroups = useMemo(() => {
    const result = letter ? preLetterGroups.filter(g => letterOf(g) === letter) : preLetterGroups;
    const sorted = [...result];
    if (sort === 'name') {
      sorted.sort((a, b) => a.displayName.localeCompare(b.displayName));
    } else {
      sorted.sort((a, b) => {
        const at = a.members[0]?.created || '';
        const bt = b.members[0]?.created || '';
        return sort === 'oldest' ? at.localeCompare(bt) : bt.localeCompare(at);
      });
    }
    return sorted;
  }, [preLetterGroups, letter, sort]);

  const hasFilters = Boolean(query || status || advisorFilter || gemstoneFilter || testFilter || letter || deactivatedFilter !== 'active');
  const clearAll = () => { setQuery(''); setStatus(''); setAdvisorFilter(''); setGemstoneFilter(''); setTestFilter(''); setLetter(''); setDeactivatedFilter('active'); };
  const hasAnyTestHousehold = useMemo(() => scopedGroups.some(g => g.primary?.is_test), [scopedGroups]);
  const hasAnyDeactivatedClient = useMemo(() => scopedGroups.some(g => g.members.some(m => m.deactivated_at)), [scopedGroups]);

  // While searching, expand every matched multi-member household so the person
  // you searched for is visible without an extra click.
  const isExpanded = (group) => group.members.length > 1 && (!!q || expanded.has(group.key));

  const confirmAction = async () => {
    if (!pendingAction) return;
    setActionError(null);
    try {
      if (pendingAction.type === 'reactivate') {
        await reactivateClient({ id: pendingAction.client.public_id }).unwrap();
      } else {
        await deactivateClient({ id: pendingAction.client.public_id }).unwrap();
      }
      setPendingAction(null);
    } catch (e) {
      setActionError(e?.data?.detail || `Could not ${pendingAction.type} that client.`);
    }
  };

  return (
    <div style={{ background: work.canvas, minHeight: '100vh', padding: isMobile ? '1rem' : '1.5rem 1.75rem', color: work.ink }}>

      {/* Header */}
      <div style={{
        background: work.surface,
        border: `1px solid ${work.hairline}`,
        borderTop: `3px solid ${work.deepBlue}`,
        borderRadius: 6,
        padding: isMobile ? '1.1rem 1.1rem' : '1.4rem 1.6rem',
        marginBottom: '1.25rem',
        display: 'flex', alignItems: 'center', gap: '0.85rem', flexWrap: 'wrap',
      }}>
        <div style={{
          background: work.deepBlue, color: 'white', width: 44, height: 44,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 6, flexShrink: 0,
        }}>
          <i className="bi bi-people-fill" style={{ fontSize: '1.3rem' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: work.muted, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', margin: '0 0 2px' }}>
            Wealth Management
          </p>
          <h3 style={{ color: work.ink, fontWeight: 800, margin: 0, fontSize: isMobile ? '1.2rem' : '1.4rem', lineHeight: 1.15 }}>
            Portal Clients
          </h3>
        </div>
        <button
          type="button"
          onClick={() => navigate('/work/clients/new')}
          style={{
            background: work.goldenrod, color: work.deepBlue, border: 0,
            borderRadius: 4, padding: '0.55rem 1rem',
            fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
          }}
        >
          <i className="bi bi-plus-lg" /> New portal client
        </button>
      </div>

      {/* Toolbar: search + sort */}
      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 220 }}>
          <i className="bi bi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: work.muted, fontSize: '0.85rem' }} />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search households or members by name, email, or HubSpot ID…"
            style={{
              width: '100%', padding: '0.5rem 0.6rem 0.5rem 2rem',
              border: `1px solid ${work.hairlineStrong}`, borderRadius: 6,
              fontSize: '0.85rem', color: work.ink, outline: 'none',
            }}
          />
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort" style={selectStyle()}>
          <option value="recent">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">By household (A–Z)</option>
        </select>
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" title="Status" style={selectStyle()}>
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="in_progress">In progress</option>
        </select>
        {presentAdvisors.length > 0 && (
          <select value={advisorFilter} onChange={(e) => setAdvisorFilter(e.target.value)} aria-label="Advisor" title="Advisor" style={selectStyle()}>
            <option value="">All advisors</option>
            {presentAdvisors.map(name => <option key={name} value={name}>{name}</option>)}
          </select>
        )}
        {presentGemstones.length > 0 && (
          <select value={gemstoneFilter} onChange={(e) => setGemstoneFilter(e.target.value)} aria-label="Gemstone" title="Gemstone" style={selectStyle()}>
            <option value="">All gemstones</option>
            {presentGemstones.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        )}
        {hasAnyTestHousehold && (
          <select value={testFilter} onChange={(e) => setTestFilter(e.target.value)} aria-label="Test households" title="Test households" style={selectStyle()}>
            <option value="">Test + real</option>
            <option value="test">Test only</option>
            <option value="real">Real only</option>
          </select>
        )}
        {hasAnyDeactivatedClient && (
          <select value={deactivatedFilter} onChange={(e) => setDeactivatedFilter(e.target.value)} aria-label="Deactivated clients" title="Deactivated clients" style={selectStyle()}>
            <option value="active">Active only</option>
            <option value="deactivated">Deactivated only</option>
            <option value="all">Active + deactivated</option>
          </select>
        )}
        {hasFilters && (
          <button type="button" onClick={clearAll} style={{ background: 'transparent', border: `1px solid ${work.hairlineStrong}`, borderRadius: 6, padding: '0.4rem 0.6rem', fontSize: '0.82rem', color: work.deepBlue, cursor: 'pointer', fontWeight: 700 }}>
            <i className="bi bi-x-circle me-1" />Clear
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: work.muted, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {filteredGroups.length} of {groups.length} household{groups.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* Alphabetical jump strip (by household last name) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginBottom: '1rem' }}>
        <button
          type="button"
          onClick={() => setLetter('')}
          style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem', fontWeight: 700, border: `1px solid ${work.hairline}`, borderRadius: 4, cursor: 'pointer', background: !letter ? work.deepBlue : work.surface, color: !letter ? 'white' : work.deepBlue }}
        >
          All
        </button>
        {LETTERS.map((L) => {
          const present = letterSet.has(L);
          const active = letter === L;
          return (
            <button
              key={L}
              type="button"
              disabled={!present}
              onClick={() => setLetter(active ? '' : L)}
              style={{
                width: 26, padding: '0.15rem 0', fontSize: '0.75rem', fontWeight: 700, borderRadius: 4,
                border: `1px solid ${work.hairline}`, cursor: present ? 'pointer' : 'default',
                background: active ? work.deepBlue : work.surface,
                color: active ? 'white' : (present ? work.deepBlue : work.hairlineStrong),
              }}
            >
              {L}
            </button>
          );
        })}
      </div>

      {/* List — one row per household, expandable to its members */}
      <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 6, overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: work.muted }}>Loading…</div>
        ) : filteredGroups.length === 0 ? (
          <div style={{ padding: '2.5rem 1rem', textAlign: 'center', color: work.muted, fontSize: '0.875rem' }}>
            {groups.length === 0 ? (
              <>
                <i className="bi bi-people" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.4rem', opacity: 0.4 }} />
                No client accounts yet. Click <strong>New portal client</strong> to set one up.
              </>
            ) : (
              <>No households match these filters.</>
            )}
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {filteredGroups.map(group => {
              const single = group.members.length === 1;
              const open = isExpanded(group);
              const goToPrimary = () => group.primary && navigate(`/work/clients/${group.primary.public_id}`);
              const onHeaderActivate = single ? goToPrimary : () => toggleExpand(group.key);
              return (
                <li key={group.key} style={{ borderBottom: `1px solid ${work.hairline}` }}>
                  {/* Household header row */}
                  <div
                    onClick={onHeaderActivate}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onHeaderActivate(); } }}
                    role="button"
                    tabIndex={0}
                    style={{
                      padding: '0.85rem 1rem',
                      display: 'flex', alignItems: 'center', gap: '0.85rem',
                      flexWrap: 'wrap', cursor: 'pointer',
                      background: open ? 'rgba(0,105,166,0.04)' : 'transparent',
                    }}
                  >
                    <i
                      className={`bi bi-${single ? 'person-fill' : (open ? 'dash-square' : 'plus-square')}`}
                      style={{ color: single ? work.tealBlue : work.deepBlue, fontSize: '1rem', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, color: work.ink, fontSize: '0.95rem' }}>
                          {group.displayName}
                        </span>
                        <StatusPill isActive={group.isActive} />
                        {group.primary && <GemstoneBadge gemstone={group.primary.gemstone} />}
                        {group.primary?.is_test && <TestBadge />}
                        {group.primary?.deactivated_at && <DeactivatedBadge />}
                        {!single && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                            background: 'rgba(208,155,44,0.18)', color: work.goldenrod,
                            fontWeight: 700, fontSize: '0.65rem',
                            textTransform: 'uppercase', letterSpacing: '0.5px',
                            padding: '0.15rem 0.5rem', borderRadius: 4, whiteSpace: 'nowrap',
                          }}>
                            <i className="bi bi-house-heart" /> {group.members.length} members
                          </span>
                        )}
                      </div>
                      {/* Member preview (names) + advisor */}
                      <div style={{ color: work.muted, fontSize: '0.8rem', marginTop: 2 }}>
                        {group.members.map((m, idx) => (
                          <React.Fragment key={m.id}>
                            <span style={{ color: idx === 0 ? work.ink : work.muted }}>
                              {fullName(m.first_name, m.last_name, m.email)}
                            </span>
                            {idx < group.members.length - 1 ? ', ' : ''}
                          </React.Fragment>
                        ))}
                      </div>
                      {group.primary && (group.primary.advisor_name || group.primary.advisor_name_2) && (
                        <div style={{ color: work.muted, fontSize: '0.75rem', marginTop: 2 }}>
                          <i className="bi bi-person-badge me-1" />
                          {[group.primary.advisor_name, group.primary.advisor_name_2].filter(Boolean).join(' · ')}
                        </div>
                      )}
                      <div style={{ marginTop: 2, display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <StagePill stage={group.primary?.workflow_status} />
                        <DocCountBadge count={group.docCount} />
                      </div>
                    </div>
                    {single && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const m = group.members[0];
                          setPendingAction({ client: m, type: m.deactivated_at ? 'reactivate' : 'deactivate' });
                          setActionError(null);
                        }}
                        aria-label={`${group.primary?.deactivated_at ? 'Reactivate' : 'Deactivate'} ${group.displayName}`}
                        title={group.primary?.deactivated_at ? 'Reactivate this client' : 'Deactivate this client'}
                        style={{ background: 'transparent', border: `1px solid ${work.hairline}`, color: group.primary?.deactivated_at ? work.tealBlue : work.red, borderRadius: 4, padding: '0.3rem 0.55rem', fontSize: '0.85rem', cursor: 'pointer' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = group.primary?.deactivated_at ? 'rgba(0,105,166,0.06)' : 'rgba(176,0,32,0.06)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <i className={`bi bi-${group.primary?.deactivated_at ? 'arrow-counterclockwise' : 'slash-circle'}`} />
                      </button>
                    )}
                    <i className={`bi bi-chevron-${single ? 'right' : (open ? 'up' : 'down')}`} style={{ color: work.muted }} />
                  </div>

                  {/* Drill-down: household members */}
                  {!single && open && (
                    <div style={{ background: work.canvas, borderTop: `1px solid ${work.hairline}` }}>
                      {group.members.map(m => (
                        <div
                          key={m.id}
                          onClick={() => navigate(`/work/clients/${m.public_id}`)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/work/clients/${m.public_id}`); } }}
                          role="button"
                          tabIndex={0}
                          style={{
                            padding: '0.65rem 1rem 0.65rem 2.6rem',
                            borderTop: `1px solid ${work.hairline}`,
                            display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', cursor: 'pointer',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 700, color: work.ink, fontSize: '0.9rem' }}>
                                {fullName(m.first_name, m.last_name, m.email)}
                              </span>
                              <AssocBadge association={m.association} />
                              <StatusPill isActive={!!m.is_active} />
                              {m.deactivated_at && <DeactivatedBadge />}
                            </div>
                            <div style={{ color: work.muted, fontSize: '0.78rem', marginTop: 1 }}>
                              {m.email || <em>Name-only member (no login)</em>}
                              {m.phone && <span> · {m.phone}</span>}
                            </div>
                            <div style={{ marginTop: 2, display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                              <StagePill stage={m.workflow_status} />
                              <DocCountBadge count={m.uploaded_document_count || 0} />
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingAction({ client: m, type: m.deactivated_at ? 'reactivate' : 'deactivate' });
                              setActionError(null);
                            }}
                            aria-label={`${m.deactivated_at ? 'Reactivate' : 'Deactivate'} ${fullName(m.first_name, m.last_name, m.email)}`}
                            title={m.deactivated_at ? 'Reactivate this client' : 'Deactivate this client'}
                            style={{ background: 'transparent', border: `1px solid ${work.hairline}`, color: m.deactivated_at ? work.tealBlue : work.red, borderRadius: 4, padding: '0.3rem 0.55rem', fontSize: '0.85rem', cursor: 'pointer' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = m.deactivated_at ? 'rgba(0,105,166,0.06)' : 'rgba(176,0,32,0.06)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            <i className={`bi bi-${m.deactivated_at ? 'arrow-counterclockwise' : 'slash-circle'}`} />
                          </button>
                          <i className="bi bi-chevron-right" style={{ color: work.muted }} />
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Deactivate / reactivate confirmation modal */}
      {pendingAction && (() => {
        const isReactivate = pendingAction.type === 'reactivate';
        const client = pendingAction.client;
        return (
          <div
            onClick={() => !isActing && setPendingAction(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(10,41,71,0.55)',
              zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ background: 'white', borderRadius: 6, width: '100%', maxWidth: 480, boxShadow: '0 12px 40px rgba(10,41,71,0.3)', overflow: 'hidden' }}
            >
              <div style={{ padding: '1.1rem 1.25rem', borderBottom: `1px solid ${work.hairline}`, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <i className={`bi bi-${isReactivate ? 'arrow-counterclockwise' : 'exclamation-triangle-fill'}`} style={{ color: isReactivate ? work.tealBlue : work.red, fontSize: '1.2rem' }} />
                <h5 style={{ color: work.ink, fontWeight: 800, margin: 0, fontSize: '1.05rem' }}>
                  {isReactivate ? 'Reactivate this client?' : 'Deactivate this client?'}
                </h5>
              </div>
              <div style={{ padding: '1rem 1.25rem' }}>
                <p style={{ margin: '0 0 0.65rem', color: work.ink, fontSize: '0.95rem' }}>
                  <strong>{fullName(client.first_name, client.last_name, client.email)}</strong>
                </p>
                <p style={{ margin: '0 0 0.85rem', color: work.muted, fontSize: '0.85rem' }}>
                  {isReactivate
                    ? 'They’ll be able to log in again and reappear on the default Clients list, picking back up right where they left off.'
                    : <>They’ll no longer be able to log in and will drop off the default Clients list, but their document checklist, uploads, and household ties are kept exactly as-is for compliance — nothing is deleted. You can reactivate them from the Deactivated filter at any time.</>}
                </p>
                {actionError && (
                  <div style={{ background: 'rgba(176,0,32,0.06)', border: `1px solid ${work.red}`, color: work.red, borderRadius: 4, padding: '0.45rem 0.7rem', fontSize: '0.8rem', marginBottom: '0.65rem' }}>
                    {actionError}
                  </div>
                )}
              </div>
              <div style={{ padding: '0.85rem 1.25rem', borderTop: `1px solid ${work.hairline}`, display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setPendingAction(null)}
                  disabled={isActing}
                  style={{
                    background: 'transparent', color: work.deepBlue,
                    border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                    padding: '0.45rem 0.95rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmAction}
                  disabled={isActing}
                  style={{
                    background: isReactivate ? work.tealBlue : work.red, color: 'white', border: 0, borderRadius: 4,
                    padding: '0.45rem 0.95rem', fontWeight: 700, fontSize: '0.85rem',
                    cursor: isActing ? 'wait' : 'pointer',
                  }}
                >
                  <i className={`bi bi-${isReactivate ? 'arrow-counterclockwise' : 'slash-circle'} me-1`} />
                  {isActing ? (isReactivate ? 'Reactivating…' : 'Deactivating…') : (isReactivate ? 'Reactivate client' : 'Deactivate client')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
